/** Formes des réponses de l'API d'administration RBAC (phase 4). */

export type ScopeType = 'global' | 'competition' | 'stage'

export interface Role {
  id: string
  code: string
  label: string
  description: string | null
  portal: 'admin' | 'candidate'
  isSystem: boolean
  requiresMfa: boolean
  isActive: boolean
  defaultScope: ScopeType
  permissionsCount?: number
  usersCount?: number
  permissions?: string[]
}

export interface PermissionModule {
  code: string
  label: string
  permissions: { code: string; label: string; sensitive: boolean }[]
}

export interface SodRule {
  code: string
  a: string
  b: string
  mode: 'block' | 'warn'
  scope: ScopeType
  message: string
}

export interface AdminUser {
  id: string
  email: string
  phone: string | null
  firstName: string | null
  lastName: string | null
  status: 'pending' | 'active' | 'disabled' | 'locked'
  mfaEnabled: boolean
  lastLoginAt: string | null
  createdAt: string | null
  roles?: { code: string; label: string; scopeType: ScopeType }[]
}

export interface Assignment {
  id: number
  role: { id: string; code: string; label: string; isActive: boolean }
  scopeType: ScopeType
  scopeId: string | null
  /** Concours ou étape concernés (libellé lisible). */
  scopeLabel?: string | null
  validFrom: string | null
  validUntil: string | null
  grantedAt: string
  grantedBy: string | null
  grantReason: string | null
  revokedAt: string | null
  revokedBy: string | null
  revokeReason: string | null
}

export interface AdminUserDetail extends AdminUser {
  assignments: Assignment[]
  effective: {
    global: string[]
    scoped: Record<string, Record<string, string[]>>
    requiresMfa: boolean
  }
  invitation: { expiresAt: string; acceptedAt: string | null } | null
}

export interface Paginated<T> {
  data: T[]
  meta: { total: number; perPage: number; currentPage: number; lastPage: number }
}

export const SCOPE_LABELS: Record<ScopeType, string> = {
  global: 'Global (tous les concours)',
  competition: 'Un concours',
  stage: 'Une étape de sélection',
}

export const USER_STATUS: Record<
  AdminUser['status'],
  { label: string; variant: 'success' | 'warning' | 'neutral' | 'danger' }
> = {
  active: { label: 'Actif', variant: 'success' },
  pending: { label: 'Invitation en attente', variant: 'warning' },
  disabled: { label: 'Désactivé', variant: 'neutral' },
  locked: { label: 'Verrouillé', variant: 'danger' },
}

export function fullName(user: {
  firstName: string | null
  lastName: string | null
  email: string
}) {
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email
}

export const dateFmt = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })
export const formatDate = (value: string | null | undefined) =>
  value ? dateFmt.format(new Date(value)) : '—'
